// SECURITY NOTE: Tokens are stored in localStorage as an accepted risk.
// The recommended production approach is httpOnly cookies to eliminate XSS
// token theft. The current localStorage approach relies on Content Security
// Policy (CSP) headers to mitigate XSS. If migrating to cookies, update the
// gateway to set Set-Cookie headers and remove client-side token management.
// See: OWASP Token Storage Cheat Sheet.
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { api, apiClient } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { PERMS } from '../lib/permissions'
import i18n from '../i18n/config'

interface User {
  userId: number
  username: string
  email: string
  fullName: string
  firstName: string
  lastName: string
  roles: string[]
  facilityIds: number[]
  timezone?: string | null
}

// Shape of /auth/profile response after camelCase conversion.
interface ProfileResponse {
  userId: number
  username: string
  email: string
  fullName: string
  firstName: string
  lastName: string
  roles: string[]
  facilityIds: number[]
  mfaEnabled: boolean
  /** R-2.8: false for SSO-only accounts. Optional on the client type because
   *  a cached SPA build may briefly talk to a gateway that predates it —
   *  absent means "unknown", and the break-glass dialog falls back to the
   *  password field (today's behaviour) rather than stranding the clinician. */
  hasPassword?: boolean
  timezone: string | null
  /** F3 login departments. Optional on the client type because a token minted
   *  before F3 shipped still deserializes server-side (the claims default), and
   *  a cached SPA build may briefly talk to either gateway version. */
  departmentIds?: number[]
  activeDepartmentId?: number | null
}

// Client-side fallback: derive permissions from role names when backend
// does not include a permissions array in the login response.
// Client-side fallback permission map. Used ONLY when the server login
// response does not include a `permissions[]` array. Every code here
// MUST match an actual backend Permission.permission_code (see
// seed_001 + the Permission rows in 0700_reference_data.sql).
//
// Mapping key: role_code from the Role table, lowercased.
const ROLE_PERMISSION_MAP: Record<string, string[]> = {
  sys_admin: [
    // Least-privilege set — mirrors DB role 1 after the 1110 migration:
    // system administration (incl. license-key admin, which no role could
    // reach between 1070 deleting the row and 1110 restoring it) + audit
    // oversight + audited emergency path. No patient-data permission: with
    // the SYS_ADMIN_ALL bypass gone the API 403s every clinical endpoint,
    // and this map keeps the UI aligned.
    PERMS.SYS_ADMIN_ALL, PERMS.USER_MANAGE, PERMS.LICENSING_MANAGE,
    PERMS.DEPARTMENT_MANAGE, PERMS.LOCATION_MANAGE,
    PERMS.SERVICE_TAXONOMY_MANAGE, PERMS.INTERFACE_OPERATE,
    PERMS.AUDIT_VIEW, PERMS.AUDIT_VIEW_USER, PERMS.AUDIT_EXPORT,
    PERMS.NOTIFICATION_MANAGE, PERMS.BREAK_GLASS,
  ],
  // Legacy "admin" alias for the seeded test user — same least-privilege set.
  admin: [
    PERMS.SYS_ADMIN_ALL, PERMS.USER_MANAGE, PERMS.LICENSING_MANAGE,
    PERMS.DEPARTMENT_MANAGE, PERMS.LOCATION_MANAGE,
    PERMS.SERVICE_TAXONOMY_MANAGE, PERMS.INTERFACE_OPERATE,
    PERMS.AUDIT_VIEW, PERMS.AUDIT_VIEW_USER, PERMS.AUDIT_EXPORT,
    PERMS.NOTIFICATION_MANAGE, PERMS.BREAK_GLASS,
  ],
  physician: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY,
    PERMS.CONSENT_MANAGE, PERMS.RESTRAINT_ORDER, PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW,
  ],
  nurse: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_ADMINISTER, PERMS.MED_RECONCILE, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.CONSENT_MANAGE, PERMS.BLOOD_ADMIN, PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_VIEW,
  ],
  pharmacist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.MED_VIEW, PERMS.MED_VERIFY, PERMS.MED_RECONCILE, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  lab_tech: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW, PERMS.RESULT_ENTER,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  radiologist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW, PERMS.RESULT_ENTER, PERMS.RESULT_VERIFY,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  reg_clerk: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE,
    PERMS.CONSENT_MANAGE,
  ],
  billing: [
    PERMS.PATIENT_VIEW,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE, PERMS.BILLING_PAYMENT,
  ],
  // Legacy alias
  billing_clerk: [
    PERMS.PATIENT_VIEW,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE, PERMS.BILLING_PAYMENT,
  ],
  med_records: [
    PERMS.PATIENT_VIEW,
    PERMS.DOC_VIEW,
  ],
  // Legacy alias
  front_desk: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.CONSENT_MANAGE,
  ],
  er_physician: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY,
    PERMS.CONSENT_MANAGE, PERMS.RESTRAINT_ORDER, PERMS.REFERRAL_CREATE,
  ],
  resident: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    // Denied: ORDER_SIGN, DOC_SIGN, ENCOUNTER_DISCHARGE, RESULT_SIGN
  ],
  np_pa: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.DIET_ORDER_CREATE, PERMS.CONSENT_MANAGE, PERMS.RESTRAINT_ORDER, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW,
  ],
  charge_nurse: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_ADMINISTER, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.ANALYTICS_VIEW,
    PERMS.BREAK_GLASS, PERMS.BLOOD_ADMIN, PERMS.TRANSFER_MANAGE,
    PERMS.CONSENT_MANAGE, PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_VIEW,
  ],
  him: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW,
    PERMS.DOC_VIEW,
    PERMS.PATIENT_MERGE, PERMS.AUDIT_VIEW, PERMS.BULK_EXPORT,
  ],
  quality_officer: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.ANALYTICS_VIEW, PERMS.AUDIT_VIEW,
  ],
  social_worker: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
    PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW,
  ],
  transcriptionist: [
    PERMS.PATIENT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
  ],
  ext_provider: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  // Allied Health
  dietitian: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.MED_RECONCILE,
    PERMS.SCHEDULE_VIEW,
    PERMS.DIET_ORDER_CREATE,
  ],
  resp_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  phys_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  occ_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  speech_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  // Surgical
  surgeon: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY, PERMS.CONSENT_MANAGE,
  ],
  anesthesiologist: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY, PERMS.CONSENT_MANAGE,
  ],
  // APAC-Specific
  midwife: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
  ],
  trad_med: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN,
    PERMS.SCHEDULE_VIEW,
  ],
  dentist: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN,
    PERMS.SCHEDULE_VIEW,
    PERMS.CONSENT_MANAGE,
  ],
  // Administrative/Support
  it_support: [
    PERMS.SYS_ADMIN_ALL, PERMS.USER_MANAGE, PERMS.AUDIT_VIEW,
  ],
  dept_head: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.MED_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.ANALYTICS_VIEW, PERMS.AUDIT_VIEW,
  ],
  bed_manager: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.SCHEDULE_VIEW,
    PERMS.TRANSFER_MANAGE,
  ],
  interpreter: [
    PERMS.PATIENT_VIEW,
  ],
  med_coder: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.BILLING_VIEW,
  ],
  insurance_coord: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  // Patient-Facing
  patient_portal: [
    PERMS.PORTAL_OWN_DATA,
  ],
  caregiver: [
    PERMS.PORTAL_OWN_DATA,
  ],
  // Operational
  infection_ctrl: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
    PERMS.ANALYTICS_VIEW,
    PERMS.INFECTION_VIEW,
  ],
  research_coord: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
    PERMS.RESEARCH_MANAGE,
  ],
}

function derivePermissionsFromRoles(roleNames: string[]): string[] {
  const permissionSet = new Set<string>()
  for (const role of roleNames) {
    const key = role.toLowerCase().replace(/[\s-]/g, '_')
    const perms = ROLE_PERMISSION_MAP[key]
    if (perms) {
      for (const p of perms) permissionSet.add(p)
    }
  }
  return Array.from(permissionSet)
}

// Extract the server-computed permission list from a JWT access token.
// The gateway puts the full, facility-scoped permission set into the
// token's `permissions` claim at login time, so this is the most
// accurate and always-up-to-date source. Falls back to an empty array
// if the token is malformed.
function permissionsFromToken(token: string): string[] {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return []
    // base64url → base64 (JWT uses URL-safe base64 without padding)
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = payloadB64.length % 4
    const padded = pad === 0 ? payloadB64 : payloadB64 + '='.repeat(4 - pad)
    const payload = JSON.parse(atob(padded))
    return Array.isArray(payload.permissions) ? payload.permissions : []
  } catch {
    return []
  }
}

// Why a login attempt failed. `mfa_required` means the credentials were valid
// but the account has TOTP enabled and the caller must resubmit with a code;
// `invalid_mfa_code` means the resubmitted code was wrong. The Login page uses
// the reason to reveal the MFA-code step instead of showing "invalid password".
export type LoginFailureReason =
  | 'mfa_required'
  | 'invalid_mfa_code'
  | 'invalid_credentials'
  | 'seat_cap_reached'

// eslint-disable-next-line react-refresh/only-export-components
export class LoginError extends Error {
  reason: LoginFailureReason
  constructor(reason: LoginFailureReason) {
    super(reason)
    this.name = 'LoginError'
    this.reason = reason
  }
}

interface AuthContextType {
  user: User | null
  login: (
    username: string,
    password: string,
    mfaCode?: string
  ) => Promise<{ mfaSetupRequired: boolean }>
  /** Directory login (S4): same shapes as password login — the gateway
   *  answers the same MFA states, surfaced as the same LoginErrors. */
  loginLdap: (
    directoryId: number,
    username: string,
    password: string,
    mfaCode?: string
  ) => Promise<{ mfaSetupRequired: boolean }>
  /** SSO callback (S4): exchange the IdP code/state for a session. The
   *  response carries no user object, so the profile round-trip fills it
   *  in. Failures throw the raw request error (the catcher surfaces the
   *  server message) — no LoginError mapping applies: there is no MFA
   *  step and no password to be wrong. */
  completeSsoCallback: (
    code: string,
    state: string
  ) => Promise<{ mfaSetupRequired: boolean }>
  /** SAML exchange (S5): the ACS validated the assertion and redirected
   *  here with a single-use artifact — swap it for a session. Same shape
   *  and failure contract as `completeSsoCallback`. */
  completeSamlExchange: (
    artifact: string
  ) => Promise<{ mfaSetupRequired: boolean }>
  logout: () => Promise<void>
  /** F3 Change Context. Lives here rather than in the calling component
   *  because switching RE-MINTS the session: the new tokens must replace the
   *  old ones and the store must rehydrate from the server, which is exactly
   *  what `login` already does. A caller that only POSTed and set some local
   *  state would leave the old department in the token still being stamped. */
  switchDepartment: (departmentId: number) => Promise<void>
  isAuthenticated: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Populate local state + Zustand store from a /auth/profile response.
  // Shared by mount-time session check and post-login flow so there's
  // one canonical place that decides what "logged-in user" means.
  //
  // `preferredLanguage` is optional because /auth/profile doesn't carry
  // it yet — the value flows in via /auth/login's UserInfo payload and
  // is forwarded here so the persisted zustand session record matches
  // what the user picked at login time.
  const hydrateFromProfile = (
    profile: ProfileResponse,
    permissions: string[],
    preferredLanguage?: string | null,
  ) => {
    setUser({
      userId: profile.userId,
      username: profile.username,
      email: profile.email,
      fullName: profile.fullName,
      firstName: profile.firstName,
      lastName: profile.lastName,
      roles: profile.roles,
      facilityIds: profile.facilityIds,
      timezone: profile.timezone,
    })
    useAuthStore.getState().setUser({
      userId: profile.userId,
      username: profile.username,
      email: profile.email,
      fullName: profile.fullName,
      roleNames: profile.roles,
      facilityIds: profile.facilityIds,
      departmentIds: profile.departmentIds ?? [],
      activeDepartmentId: profile.activeDepartmentId ?? null,
      isActive: true,
      isMfaEnabled: profile.mfaEnabled,
      hasPassword: profile.hasPassword ?? true,
      timezone: profile.timezone,
      preferredLanguage: preferredLanguage ?? null,
    })
    useAuthStore.getState().setPermissions(permissions)
  }

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setIsLoading(false)
        return
      }

      // Seed local state from the persisted zustand store so
      // ProtectedRoute doesn't flash /login while /auth/profile
      // is in flight — or if that call fails transiently (429,
      // 5xx, network). The /auth/profile response below will
      // overwrite this with fresh data on success.
      const persisted = useAuthStore.getState().user
      if (persisted) {
        setUser({
          userId: persisted.userId,
          username: persisted.username,
          email: persisted.email,
          fullName: persisted.fullName,
          firstName: '',
          lastName: '',
          roles: persisted.roleNames,
          facilityIds: persisted.facilityIds,
          // `?? null` keeps shape parity with the post-/auth/profile write below: legacy
          // localStorage entries that predate MR B may lack the field; coerce undefined→null
          // so user.timezone is always `string | null`, never `undefined`.
          timezone: persisted.timezone ?? null,
        })
      }

      try {
        const response = await apiClient.get<ProfileResponse>('/auth/profile')
        const profile = response.data
        // Prefer permissions from the JWT claim (server-computed,
        // facility-scoped). Fall back to role-name → code mapping
        // only if the token is malformed.
        const tokenPerms = permissionsFromToken(token)
        const permissions = tokenPerms.length > 0
          ? tokenPerms
          : derivePermissionsFromRoles(profile.roles)
        hydrateFromProfile(profile, permissions)
      } catch (error) {
        // Only clear tokens when the server definitively rejected
        // the token (401). Transient errors (429 rate limit, 5xx,
        // network) must not log the user out — the token is still
        // valid and we already seeded local state from zustand.
        const status =
          (error as { response?: { status?: number } })?.response?.status
        if (status === 401) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          useAuthStore.getState().logout()
          setUser(null)
        }
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  // Establish a session from any login-shaped response (password, LDAP,
  // SSO callback): persist tokens, seed locale, fetch the profile, hydrate.
  // The SSO callback carries no user object, so `user`/`permissions` read
  // undefined there and the JWT-claim + detector fallbacks apply.
  const establishSession = async (data: {
    accessToken: string
    refreshToken: string
    user?: { preferredLanguage?: string | null }
    permissions?: string[]
    mfaSetupRequired?: boolean
  }) => {
    const { accessToken, refreshToken } = data

    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
    useAuthStore.getState().setTokens(accessToken, refreshToken)

    // R-6.5 Phase 0: seed i18next from UserInfo.preferredLanguage
    // BEFORE the /auth/profile round-trip so the post-login redirect
    // renders in the user's chosen locale on first paint. Falls back
    // to whatever LanguageDetector already resolved (localStorage →
    // navigator → htmlTag) if the backend didn't set the column.
    // We also persist into `vynaemr-language` so a hard refresh
    // skips the detector's navigator/htmlTag steps entirely.
    const preferredLanguage: string | null | undefined = data.user?.preferredLanguage
    if (preferredLanguage) {
      await i18n.changeLanguage(preferredLanguage)
      localStorage.setItem('vynaemr-language', preferredLanguage)
    }

    // Fetch the full profile — the login response only carries
    // token claims (user_id, username, email, roles), not the
    // user's display name. /auth/profile is the source of truth.
    const profileResponse = await apiClient.get<ProfileResponse>('/auth/profile')
    const profile = profileResponse.data

    // Permission source, in order of preference:
    //   1. permissions array on the login response (if the backend
    //      starts returning it)
    //   2. the JWT's `permissions` claim (always populated by the
    //      gateway — this is the canonical server-computed list)
    //   3. client-side role-name → code mapping (last-resort fallback
    //      when the token can't be parsed)
    const serverPermissions: string[] | undefined = data.permissions
    const tokenPermissions = permissionsFromToken(accessToken)
    const permissions = (serverPermissions && serverPermissions.length > 0)
      ? serverPermissions
      : tokenPermissions.length > 0
        ? tokenPermissions
        : derivePermissionsFromRoles(profile.roles)

    hydrateFromProfile(profile, permissions, preferredLanguage)

    // SEC-05: the org MFA mandate applies and the user hasn't enrolled TOTP —
    // this session is RESTRICTED (empty roles/permissions) until MFA setup
    // completes. Tell the caller so it routes straight to enrollment.
    return { mfaSetupRequired: data.mfaSetupRequired === true }
  }

  // Map a password-shaped login failure to the typed LoginError the Login
  // page switches on. Error responses are NOT camelCased by the
  // interceptor, so read the raw wire `error` code.
  const toLoginError = (err: unknown): LoginError => {
    if (err instanceof LoginError) return err
    const serverError = (err as { response?: { data?: { error?: string } } })?.response
      ?.data?.error
    if (serverError === 'mfa_required') return new LoginError('mfa_required')
    if (serverError === 'invalid_mfa_code') return new LoginError('invalid_mfa_code')
    if (serverError === 'seat_cap_reached') return new LoginError('seat_cap_reached')
    return new LoginError('invalid_credentials')
  }

  const login = async (username: string, password: string, mfaCode?: string) => {
    try {
      // Only send `mfaCode` when the caller has one — the gateway treats a
      // missing code as a first-factor attempt and answers 401 `mfa_required`
      // for MFA accounts, which we surface below as a typed LoginError.
      const body: Record<string, unknown> = { username, password }
      if (mfaCode) body.mfaCode = mfaCode
      const response = await apiClient.post('/auth/login', body)
      return await establishSession(response.data)
    } catch (err) {
      throw toLoginError(err)
    }
  }

  const loginLdap = async (
    directoryId: number,
    username: string,
    password: string,
    mfaCode?: string,
  ) => {
    try {
      const body: Record<string, unknown> = { directoryId, username, password }
      if (mfaCode) body.mfaCode = mfaCode
      const response = await apiClient.post('/auth/ldap/login', body)
      return await establishSession(response.data)
    } catch (err) {
      throw toLoginError(err)
    }
  }

  const completeSsoCallback = async (code: string, state: string) => {
    const response = await api.sso.callback(code, state)
    return await establishSession(response.data)
  }

  const completeSamlExchange = async (artifact: string) => {
    const response = await api.sso.exchangeSamlArtifact(artifact)
    return await establishSession(response.data)
  }

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      // Logout anyway even if API call fails
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
      useAuthStore.getState().logout()
    }
  }

  // F3 Change Context. The gateway re-mints the whole session in the new
  // department and re-reads the assignments from the database while doing it,
  // so a department revoked since login is refused here rather than silently
  // honoured. Tokens are swapped BEFORE the profile fetch so the fetch reports
  // the department the next request will actually carry.
  const switchDepartment = async (departmentId: number) => {
    const response = await api.auth.switchDepartment(departmentId)
    const { accessToken, refreshToken } = response.data

    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
    useAuthStore.getState().setTokens(accessToken, refreshToken)

    // Permissions and locale are unchanged by a department switch — the
    // gateway re-resolves the same role grants — so carry the current ones
    // through rather than letting hydrate null them out.
    const store = useAuthStore.getState()
    const profileResponse = await apiClient.get<ProfileResponse>('/auth/profile')
    hydrateFromProfile(profileResponse.data, store.permissions, store.user?.preferredLanguage)
  }

  const value = {
    user,
    login,
    loginLdap,
    completeSsoCallback,
    completeSamlExchange,
    logout,
    switchDepartment,
    isAuthenticated: !!user,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
